import { useEffect, useState } from "react";

import {
  FEATURE_FLAGS,
  useFeatureFlags,
} from "@hl/shared-features/lib/features/auth/hooks";
import { useSupportedChains } from "@hl/shared-features/lib/features/chain";
import { getCurrencySymbol } from "@hl/shared-features/lib/utils/currency";
import { Loader, Select, SelectProps } from "@mantine/core";
import { ethers } from "ethers";
import { base, baseSepolia } from "wagmi/chains";

import { CurrencySymbol } from "apollo/graphql.generated";

type Props = {
  onChange: (address: string, symbol: CurrencySymbol) => void;
  onLoading?: (loading: boolean) => void;
  chainId: number;
} & Omit<SelectProps, "data" | "onChange">;

export const CurrencyInput: React.FC<Props> = ({
  label,
  description,
  onChange,
  value,
  chainId,
  onLoading,
  disabled = false,
  error,
}) => {
  const enableDegenCurrency = useFeatureFlags(
    FEATURE_FLAGS.ENABLE_DEGEN_CURRENCY
  );

  const isBaseChain = [+base.id, +baseSepolia.id].includes(chainId);

  const { supportedChains, loading: loadingChains } = useSupportedChains(
    !isBaseChain || !enableDegenCurrency
  );

  const [currentValue, setCurrentValue] = useState<string | null>();

  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  useEffect(() => {
    onLoading?.(loadingChains);
  }, [loadingChains]);

  if (!enableDegenCurrency) return <></>;
  if (loadingChains) return <Loader />;

  const chain = supportedChains.find((chain) => chain.chainId === chainId);
  if (!chain || ![+base.id, +baseSepolia.id].includes(chain.chainId)) {
    return <></>;
  }

  const data = chain.supportedPaymentCurrencies.map((currency) => ({
    label: getCurrencySymbol(chainId, currency.symbol),
    value: currency.address,
  }));

  return (
    <Select
      error={error}
      label={label}
      description={description}
      placeholder="Select a currency"
      value={currentValue}
      onChange={(currency) => {
        setCurrentValue(currency);
        const paymentCurrency = chain.supportedPaymentCurrencies.find(
          (chain) =>
            currency &&
            ethers.utils.getAddress(chain.address) ===
              ethers.utils.getAddress(currency)
        );

        onChange(
          paymentCurrency?.address || "",
          paymentCurrency?.symbol || CurrencySymbol.ETH
        );
      }}
      disabled={disabled}
      styles={(theme) => ({
        dropdown: {
          outline: "none",
          border: "none",
        },
        description: {
          fontSize: theme.fontSizes.sm,
          color: theme.colors.primaryText[0],
        },
      })}
      data={data}
    />
  );
};
